import { Link } from "react-router-dom";
import SunAccent from "@/components/feature/SunAccent";
import Reveal from "@/components/base/Reveal";
import type { PublicStory } from "@/lib/publicStories";

/**
 * Ranks the other stories by how closely they relate to the current one —
 * stories in the same category score highest, followed by those written by
 * the same author and those set in the same village — so the suggestions
 * genuinely continue the thread you were just reading.
 */
function getRelatedStories(current: PublicStory, all: PublicStory[], limit = 3): PublicStory[] {
  return all
    .filter((item) => item.slug !== current.slug)
    .map((item) => ({
      item,
      score:
        (item.category === current.category ? 3 : 0) +
        (item.author === current.author ? 2 : 0) +
        (item.village === current.village ? 1 : 0),
    }))
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((entry) => entry.item);
}

export default function RelatedStories({
  current,
  all,
}: {
  current: PublicStory;
  all: PublicStory[];
}) {
  const related = getRelatedStories(current, all);

  if (related.length === 0) {
    return null;
  }

  return (
    <section className="sun-glaze bg-background-50 py-16 md:py-24">
      <div className="w-full px-4 md:px-8">
        <Reveal className="flex flex-wrap items-end justify-between gap-4 mb-10 md:mb-12">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-5">
              <SunAccent circleClassName="w-8 h-8" iconClassName="text-base" /><span className="h-px w-10 bg-accent-500"></span>
              <span className="text-xs font-semibold tracking-[0.24em] uppercase text-accent-600">
                Keep Reading
              </span>
            </div>
            <h2 className="font-heading text-3xl md:text-4xl leading-tight text-foreground-950 font-semibold">
              More Stories From the Soil
            </h2>
            <p className="mt-4 text-base md:text-lg text-foreground-700 leading-relaxed">
              Picked because they share this story's theme, village or author — so the thread you
              were just following keeps going.
            </p>
          </div>
          <Link
            to="/stories"
            className="inline-flex items-center gap-2 text-sm font-semibold text-primary-700 hover:text-primary-800 transition-colors whitespace-nowrap"
          >
            View all stories
            <i className="ri-arrow-right-line"></i>
          </Link>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {related.map((story, index) => (
            <Reveal key={story.id} delay={index * 80} className="h-full">
              <Link
                to={`/stories/${story.slug}`}
                className="group h-full bg-background-100 rounded-lg border border-background-200 overflow-hidden flex flex-col hover:border-primary-300 transition-colors cursor-pointer"
              >
                <div className="relative w-full h-44 md:h-48 overflow-hidden bg-background-200">
                  <img
                    src={story.cover}
                    width={1200}
                    height={800}
                    alt={`${story.title} — cover image for this Rythugaru farmer story from ${story.village}`}
                    title={`${story.title} - Rythugaru journal`}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    decoding="async"
                  />
                  <span className="absolute top-4 left-4 bg-background-50/95 text-foreground-800 text-[11px] font-semibold px-3 py-1.5 rounded-full whitespace-nowrap">
                    {story.category}
                  </span>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <p className="text-xs text-foreground-500">
                    {story.date} · {story.readTime}
                  </p>
                  <h3 className="font-heading text-lg text-foreground-950 font-semibold leading-snug mt-2">
                    {story.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed mt-3 line-clamp-2 flex-1">
                    {story.excerpt}
                  </p>
                  <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-primary-700 group-hover:text-primary-800">
                    Read story
                    <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1"></i>
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}